import React, { useMemo } from 'react';

import FormCheckboxGroup from '../elements/formElements/FormCheckboxGroup';
import FormGroup from '../elements/formElements/FormGroup';
import FormGroupTextEditor from '../elements/formElements/FormGroupTextEditor';
import Select, { SelectOptions } from '../elements/formElements/Select';
import { ValidationErrors } from '../helpers/ResponseErrorHelper';
import { getLabelFromName } from '../helpers/formUtils';

export type FieldChangeCallback = (name: string, value: any, type?: ConditionalProps['type']) => void;

type BaseProps = Readonly<{
    name: string;
    label?: string | JSX.Element;
    onChange?: FieldChangeCallback;
    disableAutofill?: boolean;
    options?: SelectOptions;
    required?: boolean;
    disabled?: boolean;
    min?: string | number;
    max?: string | number;
    placeholder?: string;
}>;

type ConditionalProps =
    | { type: 'number'; value: number }
    | { type: 'checkbox'; value: boolean }
    | { type: 'select'; value: number | string; options: SelectOptions }
    | { type: 'selectNumber'; value: number; options: SelectOptions }
    | { type?: 'text' | 'email' | 'password' | 'texteditor' | 'time' | 'date' | 'datetime-local'; value: string };

type SharedProps = {
    errors: ValidationErrors;
    onChange: FieldChangeCallback;
    disableAutofill?: boolean;
};

export function SmartField({
    type = null,
    name,
    value,
    errors,
    onChange,
    label = null,
    disableAutofill = false,
    options = null,
    required = false,
    disabled = false,
    min,
    max,
    placeholder,
}: BaseProps & ConditionalProps & SharedProps) {
    const handleInputChange = ({ target }: { target: HTMLInputElement | HTMLSelectElement }) => {
        if (target?.type === 'checkbox') {
            onChange(target.name, (target as HTMLInputElement).checked, type);
            return;
        }
        if (['number', 'selectNumber'].includes(type)) {
            const numberVal = parseFloat(target.value);
            if (!Number.isNaN(numberVal)) {
                onChange(target.name, numberVal, type);
                return;
            }
        }
        onChange(target.name, target.value, type);
    };

    switch (type) {
        case 'select':
        case 'selectNumber': {
            return (
                <Select
                    field={name}
                    value={(value as string | number) ?? ''}
                    label={label ?? getLabelFromName(name)}
                    options={options ?? {}}
                    error={errors[name]?.[0]}
                    handleChange={handleInputChange}
                    required={required}
                    disabled={disabled}
                    nullCaption={placeholder}
                />
            );
        }
        case 'checkbox': {
            return (
                <FormCheckboxGroup
                    checked={!!value}
                    name={name}
                    label={label ?? getLabelFromName(name)}
                    error={errors[name]?.[0]}
                    handleChange={handleInputChange}
                    disableAutofill={disableAutofill}
                    required={required}
                    disabled={disabled}
                />
            );
        }
        case 'texteditor': {
            return (
                <FormGroupTextEditor
                    field={name}
                    value={(value as string) ?? ''}
                    label={label ?? getLabelFromName(name)}
                    error={errors[name]?.[0]}
                    handleChange={(text) => onChange(name, text, type)}
                    required={required}
                    disabled={disabled}
                />
            );
        }
        default: {
            return (
                <FormGroup
                    type={type ?? 'text'}
                    field={name}
                    value={(value as string | number) ?? ''}
                    label={label ?? getLabelFromName(name)}
                    error={errors[name]?.[0]}
                    handleChange={handleInputChange}
                    disableAutofill={disableAutofill}
                    required={required}
                    disabled={disabled}
                    min={min}
                    max={max}
                    placeholder={placeholder}
                />
            );
        }
    }
}

/* eslint-disable react/jsx-props-no-spreading */

export default function useSmartField(sharedProps: SharedProps) {
    function WrappedSmartField(props: BaseProps & ConditionalProps) {
        return <SmartField {...{ ...sharedProps, ...props }} />;
    }

    return useMemo(() => WrappedSmartField, [sharedProps.errors]);
}
